/**
 * DashboardComponent — top-level shell for the dashboard page.
 *
 * Layout: vertical sidebar on the left (dashboard selector) + content on the
 * right (the selected plugin's DashboardShell). When only one dashboard is
 * registered, the sidebar is hidden and the content fills the width.
 */

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Alert, Box, Skeleton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { dashboardRegistry, visibleDashboardsFor } from "./dashboardRegistry";
import DashboardSelector from "./DashboardSelector";
import DashboardShell from "./DashboardShell";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";

interface DashboardComponentProps {
  isOpen: boolean;
  menuWidth: number;
  effectiveRole?: string;
  currentTeamId?: number;
  /**
   * Whether the user is a member of the Weblab Tracker team (FINN team 104).
   * When false, the Weblab Tracker plugin is hidden and only the FINN Revenue
   * Bridging dashboard is available.
   */
  isWeblabTrackerMember?: boolean;
  /**
   * Whether the user is a member of the SPB Finance team (FINN team 2).
   * When false, the Weblab Visual dashboard is hidden (its weblab-science
   * source data is restricted to SPB Finance internal use).
   */
  isSpbFinanceMember?: boolean;
  /**
   * The current user's numeric id. Besides being forwarded to plugins for
   * permission checks, it gates the superuser-only Audit Console plugin
   * (via isSuperuser in visibleDashboardsFor).
   */
  userId?: number;
  userAttributes?: any;
  darkMode?: boolean;
  onToggleDark?: () => void;
  /**
   * Hide the SPB Analytics entry (VESL teams without an SP/SB/SPB token). The
   * Dashboard rail item is shown to every team so Published Reports is
   * reachable; this keeps the SPB Analytics content gate as it was.
   */
  hideSpbAnalytics?: boolean;
  /**
   * Open this dashboard when the request changes (e.g. a FINN Skills link on
   * the Ask AI landing). `nonce` makes repeated requests for the same id count.
   * Ignored when the dashboard is not visible to the user.
   */
  openRequest?: { id: string; nonce: number } | null;
}

const DashboardComponent: React.FC<DashboardComponentProps> = ({
  isOpen,
  menuWidth,
  effectiveRole,
  currentTeamId,
  isWeblabTrackerMember,
  isSpbFinanceMember,
  userId,
  userAttributes,
  darkMode,
  onToggleDark,
  hideSpbAnalytics,
  openRequest,
}) => {
  const theme = useTheme();
  // Report subscriptions (F3) are behind a feature flag; My subscriptions is
  // listed only while it is on (report-subscriptions spec, Req 1.6, 2.1).
  const { flags } = useFeatureFlags();
  const reportDigestEnabled = flags.REPORT_DIGEST_ENABLED === true;

  // The registry the user can actually see. The Weblab Tracker plugin is
  // gated to members of team 104, the Weblab Visual plugin to SPB Finance
  // (team 2), the Audit Console plugin to superusers (frontend defense in
  // depth; the server pipeline stays authoritative), and My subscriptions to
  // the REPORT_DIGEST_ENABLED flag. Everyone else sees only the ungated
  // dashboards. Filtering here (rather than in the static registry) keeps the
  // gates pure functions of the user's context — see visibleDashboardsFor in
  // dashboardRegistry.ts.
  const visibleDashboards = useMemo(
    () =>
      visibleDashboardsFor(dashboardRegistry, {
        isWeblabTrackerMember,
        isSpbFinanceMember,
        userId,
        hideSpbAnalytics,
        reportDigestEnabled,
      }),
    [
      isWeblabTrackerMember,
      isSpbFinanceMember,
      userId,
      hideSpbAnalytics,
      reportDigestEnabled,
    ]
  );

  // Tracks whether the user has manually chosen a dashboard tab. Until they
  // do, we keep trying to honor the ?dashboard= deep-link param as the
  // visible-dashboard set resolves (see the deep-link effect below).
  const userSelectedRef = useRef(false);
  // Tracks whether we've already applied the ?dashboard= deep-link, so we
  // only auto-apply it once (not on every visibleDashboards change).
  const deepLinkAppliedRef = useRef(false);

  const [selectedId, setSelectedId] = useState<string>(() => {
    // Read ?dashboard= param for deep-linking to a specific plugin.
    // NOTE: on first render the async membership gate (isWeblabTrackerMember)
    // may not have resolved yet, so a deep-linked plugin (e.g. weblab-tracker)
    // can be absent from visibleDashboards here. The deep-link effect below
    // re-applies the param once the gate resolves.
    const params = new URLSearchParams(window.location.search);
    const dashParam = params.get("dashboard");
    if (dashParam && visibleDashboards.find((d) => d.id === dashParam)) {
      deepLinkAppliedRef.current = true;
      return dashParam;
    }
    return visibleDashboards.length > 0 ? visibleDashboards[0].id : "";
  });

  // Wrapper that also syncs the URL param when the user switches plugins
  const handleSelect = useCallback((id: string) => {
    userSelectedRef.current = true;
    setSelectedId(id);
    const params = new URLSearchParams(window.location.search);
    params.set("dashboard", id);
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}?${params.toString()}`
    );
  }, []);

  // Deep-link re-apply: the ?dashboard= param may point at a plugin that was
  // not yet visible on first render because its membership gate
  // (isWeblabTrackerMember) resolves asynchronously. Once the visible set
  // updates, honor the param — but only once, and never over a manual pick.
  useEffect(() => {
    if (deepLinkAppliedRef.current || userSelectedRef.current) return;
    const dashParam = new URLSearchParams(window.location.search).get(
      "dashboard"
    );
    if (dashParam && visibleDashboards.find((d) => d.id === dashParam)) {
      deepLinkAppliedRef.current = true;
      setSelectedId(dashParam);
    }
  }, [visibleDashboards]);

  // Requested dashboard (FINN Skills link): select it if the user can see it.
  useEffect(() => {
    if (!openRequest) return;
    if (visibleDashboards.find((d) => d.id === openRequest.id)) {
      deepLinkAppliedRef.current = true;
      handleSelect(openRequest.id);
    }
    // Only a new request (nonce) should re-select; the visible set changing
    // must not override a later manual pick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRequest?.nonce]);

  // Safety net: if the current selection is not in the visible set (e.g. the
  // membership gate removed it), fall back to the first available dashboard.
  useEffect(() => {
    if (
      visibleDashboards.length > 0 &&
      !visibleDashboards.find((d) => d.id === selectedId)
    ) {
      setSelectedId(visibleDashboards[0].id);
    }
  }, [selectedId, visibleDashboards]);

  const selectedPlugin = visibleDashboards.find((d) => d.id === selectedId);

  if (!isOpen) return null;

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        ml: `${menuWidth}px`,
        transition: theme.transitions.create("margin-left", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.standard,
        }),
        height: "100vh",
        overflow: "hidden",
        bgcolor: theme.palette.mode === "dark" ? "grey.900" : "#f4f6f8",
        display: "flex",
        flexDirection: "row",
      }}
    >
      {/* Left sidebar — dashboard submenu */}
      {visibleDashboards.length > 1 && (
        <DashboardSelector
          dashboards={visibleDashboards}
          selectedId={selectedId}
          onSelect={handleSelect}
        />
      )}

      {/* Right content area */}
      <Box sx={{ flex: 1, overflow: "auto", p: 3, pt: 2 }}>
        {visibleDashboards.length === 0 && (
          <Alert severity="info" sx={{ maxWidth: 480 }}>
            No dashboards available.
          </Alert>
        )}

        {selectedPlugin ? (
          <DashboardShell
            key={selectedPlugin.id}
            plugin={selectedPlugin}
            effectiveRole={effectiveRole}
            currentTeamId={currentTeamId}
            userId={userId}
            userAttributes={userAttributes}
            darkMode={darkMode}
            onToggleDark={onToggleDark}
          />
        ) : (
          visibleDashboards.length > 0 && (
            <Box sx={{ py: 4 }}>
              <Skeleton
                variant="rectangular"
                height={48}
                sx={{ mb: 2, borderRadius: 1 }}
              />
              <Skeleton
                variant="rectangular"
                height={200}
                sx={{ borderRadius: 1 }}
              />
            </Box>
          )
        )}
      </Box>
    </Box>
  );
};

export default DashboardComponent;
